'use client';

import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCreateVehicle, useUpdateVehicle, type VehicleInput } from '@/hooks/useAdminVehicles';
import type { AdminVehicle, AssetClass } from '@/lib/types';
import { RelationSelect } from './RelationSelect';
import {
  NUMBER_FIELDS,
  number,
  stateFrom,
  TEXT_FIELDS,
  text,
  type UnitFormState,
} from './unitFields';

interface UnitDialogProps {
  organizationId: string;
  /** Null creates a unit; a vehicle edits that one. */
  vehicle: AdminVehicle | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: { id: string; name: string }[];
  drivers: { id: string; name: string }[];
}

/** Every property of a unit, in one form — create and edit are the same fields. */
export function UnitDialog({
  organizationId,
  vehicle,
  open,
  onOpenChange,
  groups,
  drivers,
}: UnitDialogProps) {
  const [form, setForm] = useState<UnitFormState>(() => stateFrom(vehicle));

  const createVehicle = useCreateVehicle(organizationId);
  const updateVehicle = useUpdateVehicle(organizationId);
  const isPending = createVehicle.isPending || updateVehicle.isPending;

  useEffect(() => {
    if (open) setForm(stateFrom(vehicle));
  }, [open, vehicle]);

  const set = (key: string, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name?.trim() || !form.plateNumber?.trim()) {
      toast.error('A name and a plate number are required');
      return;
    }

    const payload: VehicleInput = {
      name: form.name.trim(),
      plateNumber: form.plateNumber.trim(),
      assetClass: form.assetClass,
      brand: text(form.brand),
      model: text(form.model),
      color: text(form.color),
      vin: text(form.vin),
      year: number(form.year),
      fuelTankVolume: number(form.fuelTankVolume),
      initialMileage: number(form.initialMileage),
      speedLimit: number(form.speedLimit),
      groupId: text(form.groupId),
      driverId: text(form.driverId),
    };

    // An allocated unit's ident belongs to the pool row that gave it, so it is
    // shown but never sent back — the allocation owns that link.
    if (!vehicle?.PartnerDevice) payload.ident = text(form.ident);

    try {
      if (vehicle) {
        await updateVehicle.mutateAsync({ id: vehicle.id, ...payload });
        toast.success(`${payload.name} updated`);
      } else {
        await createVehicle.mutateAsync(payload);
        toast.success(`${payload.name} created`);
      }
      onOpenChange(false);
    } catch (error) {
      toast.error(vehicle ? 'Could not update the unit' : 'Could not create the unit', {
        description: error instanceof Error ? error.message : 'Something went wrong',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{vehicle ? `Edit ${vehicle.name}` : 'New unit'}</DialogTitle>
            <DialogDescription>
              {vehicle?.PartnerDevice
                ? 'This unit came out of a partner pool, so its identifier is managed by the allocation.'
                : 'A unit with no identifier records nothing until one is set or a device is allocated.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4 sm:grid-cols-2">
            {[...TEXT_FIELDS, ...NUMBER_FIELDS].map((field) => (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={`unit-${field.key}`}>{field.label}</Label>
                <Input
                  id={`unit-${field.key}`}
                  type={NUMBER_FIELDS.includes(field) ? 'number' : 'text'}
                  value={form[field.key] ?? ''}
                  onChange={(event) => set(field.key, event.target.value)}
                  placeholder={field.placeholder}
                />
              </div>
            ))}

            <div className="space-y-2">
              <Label htmlFor="unit-ident">Ident (IMEI)</Label>
              <Input
                id="unit-ident"
                value={form.ident ?? ''}
                onChange={(event) => set('ident', event.target.value)}
                disabled={Boolean(vehicle?.PartnerDevice)}
                placeholder="358480081234567"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="unit-class">Asset class</Label>
              <Select
                value={form.assetClass}
                onValueChange={(value) => set('assetClass', value as AssetClass)}
              >
                <SelectTrigger id="unit-class">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="POWERED">Powered</SelectItem>
                  <SelectItem value="UNPOWERED">Unpowered</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <RelationSelect
              id="unit-group"
              label="Group"
              value={form.groupId ?? ''}
              options={groups}
              onChange={(value) => set('groupId', value)}
            />
            <RelationSelect
              id="unit-driver"
              label="Driver"
              value={form.driverId ?? ''}
              options={drivers}
              onChange={(value) => set('driverId', value)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {vehicle ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
